import api from '@/helpers/api_helper';
import { apiUrls, env, infra } from '@/constant/configure';
import typeToString from '@/utils/typeToString';
import { upperCase, groupBy, flatMap } from 'lodash';
import { getAcceptedImageMimeTypes, getAcceptedFileMimeTypes } from '@/constant/acceptedFiles';

// const env = process.env.VITE_DEPLOY_ENV ? upperCase(process.env.VITE_DEPLOY_ENV) : 'DEV';
const { s3bucket } = infra;
const baseUrl = apiUrls.proxy;

type Payload = {
    upload: File[] | Blob[];
    needToConvert?: 'Y' | 'N';
    limitedImageSize?: number;
};
const packingFilePayload = (payload: Payload) => {
    try {
        const formData = new FormData();

        Object.entries(payload).forEach(([key, value]) => {
            if (key === 'upload') {
                //@ts-ignore
                value.forEach((file) => {
                    formData.append('upload', file);
                });
            } else {
                //@ts-ignore
                if (!!value) formData.append(key, value);
            }
        });

        return formData;
    } catch (e) {
        throw e;
    }
};

declare interface UploadImageResponse {
    url: string[];
    type: string[];
    imgRatio: number[];
    src_url?: string[];
    src_type?: string[];
    upload?: string;
    msg?: string;
}

export const uploadImageToStorage = async (
    files: File[] | Blob[] | FileList,
    limitSize?: number,
): Promise<UploadImageResponse | undefined> => {
    try {
        const willConvert = !!limitSize ? 'Y' : 'N';

        let nFiles: File[] | Blob[];

        if (typeToString(files) === 'FileList') {
            nFiles = !!files ? Array.from(files) : [];
        } else {
            nFiles = (files as File[]) || [];
        }

        if (!nFiles?.length) return;

        const data: Payload = {
            upload: nFiles,
            needToConvert: willConvert,
            limitedImageSize: !!limitSize ? limitSize : undefined,
        };

        const payload = packingFilePayload(data);

        return await api
            .post<UploadImageResponse>(
                `/common/files/upload/${s3bucket.default}/image?env=${(env || 'DEV').toUpperCase()}`,
                payload,
                {
                    baseURL: baseUrl,
                },
            )
            .then((r) => {
                return r?.data || r;
            });
    } catch (e) {
        if (typeof e === 'string') {
            throw {
                message: e,
            };
        } else {
            throw e;
        }
    }
};

export const uploadSignUpImageToStorage = async (
    files: File[] | Blob[] | FileList,
    limitSize?: number,
): Promise<UploadImageResponse | undefined> => {
    try {
        const willConvert = !!limitSize ? 'Y' : 'N';

        let nFiles: File[] | Blob[];

        if (typeToString(files) === 'FileList') {
            nFiles = !!files ? Array.from(files) : [];
        } else {
            nFiles = (files as File[]) || [];
        }

        if (!nFiles?.length) return;

        const data: Payload = {
            upload: nFiles,
            needToConvert: willConvert,
            limitedImageSize: !!limitSize ? limitSize : undefined,
        };

        const payload = packingFilePayload(data);

        return await api
            .post<UploadImageResponse>(
                `/common/files/upload/${s3bucket.default}/image?env=${(env || 'DEV').toUpperCase()}`,
                payload,
                {
                    baseURL: baseUrl,
                },
            )
            .then((r) => {
                return r?.data || r;
            });
    } catch (e: any) {
        if (typeof e === 'string') {
            throw {
                message: e,
                isProxyError: true,
                placeHolder: '사본 이미지 업로드 중 오류가 발생하였습니다. 잠시 후 이미지 파일을 다시 선택해주세요.',
            };
        } else {
            throw {
                ...e,
                isProxyError: true,
                placeHolder: '사본 이미지 업로드 중 오류가 발생하였습니다. 잠시 후 이미지 파일을 다시 선택해주세요.',
            };
        }
    }
};

export const uploadFileToStorage = async (files: File[] | Blob[] | FileList, limitSize?: number) => {
    try {
        const willConvert = !!limitSize ? 'Y' : 'N';

        let nFiles: File[] | Blob[];

        if (typeToString(files) === 'FileList') {
            nFiles = !!files ? Array.from(files) : [];
        } else {
            nFiles = (files as File[]) || [];
        }

        if (!nFiles?.length) return;
        const docs = ['application', 'text'];

        // todo: 향후 docs 관련 분류 로직 보강 필요
        const groupedFiles = groupBy(nFiles, (o) => {
            const fileType = o.type.split('/')[0];

            return !docs.includes(fileType) ? fileType : 'docs';
        });

        const uploads: any[] = [];

        Object.entries(groupedFiles).forEach(([type, files]) => {
            const payload = packingFilePayload({
                upload: files,
                needToConvert: willConvert,
                limitedImageSize: !!limitSize ? limitSize : undefined,
            });

            const uploadFunc = api
                .post<UploadImageResponse['url']>(
                    `/common/files/upload/${s3bucket.default}/${type}?env=${(env || 'DEV').toUpperCase()}`,
                    payload,
                    {
                        baseURL: baseUrl,
                    },
                )
                .then((r) => {
                    //@ts-ignore
                    return r?.data?.url || [];
                });

            uploads.push(uploadFunc);
        });

        return await Promise.allSettled(uploads)
            .then((r) => {
                const isRejected = flatMap(r, (o) => o.status).includes('rejected');
                if (isRejected) throw new Error('파일 업로드 중 오류 발생');

                return flatMap(r, (o) => {
                    //@ts-ignore
                    return o?.value || [];
                });
            })
            .catch((err) => {
                throw err;
            });
    } catch (e: any) {
        if (typeof e === 'string') {
            throw {
                message: e,
            };
        } else {
            if (e instanceof Object) {
                e.placeHolder = '파일 업로드 중 오류가 발생했습니다.';
                throw e;
            } else {
                throw e;
            }
        }
    }
};

const proxyService = {
    uploadImageToStorage,
    uploadFileToStorage,
};

export default proxyService;
